import React, { useState, useEffect } from 'react';
import {
  AppBar,
  Toolbar,
  Typography,
  Button,
  Container,
  Grid,
  Card,
  CardContent,
  Box,
  IconButton,
  TextField,
  InputAdornment,
  Chip,
  Avatar,
  Rating,
  Divider,
  Paper,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  useTheme,
  useMediaQuery,
  CssBaseline,
  ThemeProvider,
  createTheme,
  GlobalStyles,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Badge,
  LinearProgress,
  Tabs,
  Tab,
  Fade,
  Slide,
  CardMedia
} from '@mui/material';

import {
  ShoppingCart,
  Store,
  Analytics,
  Payment,
  Security,
  Cloud,
  Phone,
  Speed,
  Support,
  CheckCircle,
  Star,
  Email,
  LocationOn,
  ArrowForward,
  TrendingUp,
  Inventory,
  Campaign,
  Groups,
  ExpandMore,
  Code,
  AutoAwesome,
  Palette,
  Language,
  Shield,
  Rocket,
  Timeline,
  Psychology,
  Public,
  StoreMallDirectory,
  DesignServices,
  CloudSync,
  ApiOutlined,
  Insights,
  CurrencyExchange,
  AutoGraph,
  ConnectWithoutContact,
  LiveHelp,
  School,
  MenuBook,
  VideoLibrary,
  Forum,
  OpenInNew,
  PlayArrow,
  Menu,
  Close,
  Facebook,
  Twitter,
  LinkedIn,
  Instagram,
  YouTube,
  GitHub,
  Launch,
  Favorite,
  Share,
  BookmarkBorder,
  Lightbulb,
  People,
  Devices,
  LocalShipping,
  Trending,
  Mail
} from '@mui/icons-material';

// Enhanced theme with Google Fonts and professional styling
const theme = createTheme({
  palette: {
    primary: {
      main: '#0066cc',
      light: '#4d94ff',
      dark: '#004799',
      contrastText: '#ffffff',
    },
    secondary: {
      main: '#6366f1',
      light: '#818cf8',
      dark: '#4f46e5',
      contrastText: '#ffffff',
    },
    accent: {
      main: '#f59e0b',
      light: '#fbbf24',
      dark: '#d97706',
    },
    background: {
      default: '#fafbfc',
      paper: '#ffffff',
    },
    text: {
      primary: '#1f2937',
      secondary: '#6b7280',
    },
    success: {
      main: '#10b981',
      light: '#34d399',
      dark: '#059669',
    },
    error: {
      main: '#ef4444',
      light: '#f87171',
      dark: '#dc2626',
    },
    grey: {
      50: '#f9fafb',
      100: '#f3f4f6',
      200: '#e5e7eb',
      300: '#d1d5db',
      400: '#9ca3af',
      500: '#6b7280',
      600: '#4b5563',
      700: '#374151',
      800: '#1f2937',
      900: '#111827',
    }
  },
  typography: {
    fontFamily: '"Poppins", "Inter", "Roboto", "Helvetica", "Arial", sans-serif',
    h1: {
      fontSize: '3.5rem',
      fontWeight: 700,
      lineHeight: 1.2,
      letterSpacing: '-0.02em',
    },
    h2: {
      fontSize: '2.5rem',
      fontWeight: 600,
      lineHeight: 1.3,
      letterSpacing: '-0.01em',
    },
    h3: {
      fontSize: '2rem',
      fontWeight: 600,
      lineHeight: 1.4,
    },
    h4: {
      fontSize: '1.5rem',
      fontWeight: 600,
      lineHeight: 1.4,
    },
    h5: {
      fontSize: '1.25rem',
      fontWeight: 600,
      lineHeight: 1.5,
    },
    h6: {
      fontSize: '1.125rem',
      fontWeight: 600,
      lineHeight: 1.5,
    },
    body1: {
      fontSize: '1rem',
      lineHeight: 1.6,
    },
    body2: {
      fontSize: '0.875rem',
      lineHeight: 1.6,
    },
    button: {
      fontWeight: 600,
      textTransform: 'none',
      letterSpacing: '0.025em',
    }
  },
  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: 12,
          padding: '12px 32px',
          fontSize: '1rem',
          boxShadow: 'none',
          '&:hover': {
            boxShadow: '0 8px 25px rgba(0, 0, 0, 0.15)',
            transform: 'translateY(-2px)',
          },
          transition: 'all 0.3s ease',
        },
        contained: {
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          '&:hover': {
            background: 'linear-gradient(135deg, #5a67d8 0%, #6b46c1 100%)',
          }
        }
      }
    },
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: 20,
          boxShadow: '0 4px 20px rgba(0, 0, 0, 0.08)',
          border: '1px solid rgba(0, 0, 0, 0.05)',
          transition: 'all 0.3s ease',
          '&:hover': {
            transform: 'translateY(-4px)',
            boxShadow: '0 12px 40px rgba(0, 0, 0, 0.15)',
          }
        }
      }
    }
  }
});

// Global styles with Google Fonts
const globalStyles = (
  <GlobalStyles
    styles={{
      '@import': [
        'url("https://fonts.googleapis.com/css2?family=Poppins:wght@300;400;500;600;700;800;900&display=swap")',
        'url("https://fonts.googleapis.com/css2?family=Inter:wght@300;400;500;600;700;800;900&display=swap")'
      ],
      '*': {
        margin: 0,
        padding: 0,
        boxSizing: 'border-box',
      },
      html: {
        scrollBehavior: 'smooth',
      },
      body: {
        background: 'linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)',
        minHeight: '100vh',
      },
      '.gradient-background': {
        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
      },
      '.hero-gradient': {
        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
        backgroundSize: '400% 400%',
        animation: 'gradientShift 8s ease infinite',
      },
      '@keyframes gradientShift': {
        '0%': { backgroundPosition: '0% 50%' },
        '50%': { backgroundPosition: '100% 50%' },
        '100%': { backgroundPosition: '0% 50%' },
      },
      '.glass-effect': {
        background: 'rgba(255, 255, 255, 0.1)',
        backdropFilter: 'blur(10px)',
        border: '1px solid rgba(255, 255, 255, 0.2)',
      },
      '.floating-animation': {
        animation: 'float 6s ease-in-out infinite',
      },
      '@keyframes float': {
        '0%': { transform: 'translateY(0px)' },
        '50%': { transform: 'translateY(-20px)' },
        '100%': { transform: 'translateY(0px)' },
      }
    }}
  />
);

const ShopifyvWebsite = () => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [activeSection, setActiveSection] = useState('home');
  const [scrolled, setScrolled] = useState(false);
  
  const muiTheme = useTheme();
  const isMobile = useMediaQuery(muiTheme.breakpoints.down('md'));

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToSection = (sectionId) => {
    setActiveSection(sectionId);
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
    setMobileMenuOpen(false);
  };

  // Enhanced Header Component
  const Header = () => (
    <AppBar 
      position="fixed" 
      sx={{
        background: scrolled ? 'rgba(255, 255, 255, 0.95)' : 'transparent',
        backdropFilter: scrolled ? 'blur(20px)' : 'none',
        boxShadow: scrolled ? '0 4px 20px rgba(0, 0, 0, 0.1)' : 'none',
        transition: 'all 0.3s ease',
        borderBottom: scrolled ? '1px solid rgba(0, 0, 0, 0.1)' : 'none',
      }}
    >
      <Container maxWidth="xl">
        <Toolbar sx={{ minHeight: 80, justifyContent: 'space-between' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Box
                sx={{
                  width: 50,
                  height: 50,
                  borderRadius: '12px',
                  background: 'white',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: 'white',
                  fontSize: '1.5rem',
                  fontWeight: 'bold',
                }}
              >
                <img src={require('./logo.png')} style={{
                  width: 40,
                  height: 40,
                  borderRadius: '12px',
                }}/>
              </Box>
            <Typography
              variant="h4"
              sx={{
                fontWeight: 800,
                background: scrolled 
                  ? 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)'
                  : 'linear-gradient(135deg, #ffffff 0%, #f0f0f0 100%)',
                backgroundClip: 'text',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                letterSpacing: '-0.02em',
              }}
            >
              Shopifyv
            </Typography>
          </Box>

          {!isMobile && (
            <Box sx={{ display: 'flex', gap: 1 }}>
              {[
                { id: 'home', label: 'Home' },
                { id: 'features', label: 'Features' },
                { id: 'solutions', label: 'Solutions' },
                // { id: 'testimonials', label: 'Testimonials' },
                { id: 'resources', label: 'Resources' },
                { id: 'contact', label: 'Contact' }
              ].map((item) => (
                <Button
                  key={item.id}
                  onClick={() => scrollToSection(item.id)}
                  sx={{
                    color: scrolled ? 'text.primary' : 'white',
                    fontWeight: activeSection === item.id ? 600 : 400,
                    borderRadius: 2,
                    px: 3,
                    py: 1,
                    position: 'relative',
                    '&:after': {
                      content: '""',
                      position: 'absolute',
                      bottom: 0,
                      left: '50%',
                      transform: 'translateX(-50%)',
                      width: activeSection === item.id ? '80%' : '0%',
                      height: 2,
                      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                      transition: 'width 0.3s ease',
                      borderRadius: 1,
                    }
                  }}
                >
                  {item.label}
                </Button>
              ))}
            </Box>
          )}

          {/* <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            {!isMobile && (
              <>
                <Button
                  variant="outlined"
                  sx={{
                    color: scrolled ? 'primary.main' : 'white',
                    borderColor: scrolled ? 'primary.main' : 'white',
                    '&:hover': {
                      borderColor: 'secondary.main',
                      color: 'secondary.main',
                    }
                  }}
                >
                  Sign In
                </Button>
                <Button
                  variant="contained"
                  sx={{
                    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                    '&:hover': {
                      background: 'linear-gradient(135deg, #5a67d8 0%, #6b46c1 100%)',
                    }
                  }}
                >
                  Start Free Trial
                </Button>
              </>
            )}
            
            {isMobile && (
              <IconButton
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                sx={{ color: scrolled ? 'text.primary' : 'white' }}
              >
                {mobileMenuOpen ? <Close /> : <Menu />}
              </IconButton>
            )}
          </Box> */}
        </Toolbar>

        {/* Mobile Menu */}
        {isMobile && mobileMenuOpen && (
          <Box
            sx={{
              position: 'absolute',
              top: '100%',
              left: 0,
              right: 0,
              background: 'rgba(255, 255, 255, 0.95)',
              backdropFilter: 'blur(20px)',
              borderRadius: '0 0 20px 20px',
              p: 3,
              boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1)',
            }}
          >
            {[
              { id: 'home', label: 'Home' },
              { id: 'features', label: 'Features' },
              { id: 'solutions', label: 'Solutions' },
              // { id: 'testimonials', label: 'Testimonials' },
              { id: 'resources', label: 'Resources' },
              { id: 'contact', label: 'Contact' }
            ].map((item) => (
              <Button
                key={item.id}
                fullWidth
                onClick={() => scrollToSection(item.id)}
                sx={{ justifyContent: 'flex-start', mb: 1, py: 1.5 }}
              >
                {item.label}
              </Button>
            ))}
            <Divider sx={{ my: 2 }} />
            <Button variant="outlined" fullWidth sx={{ mb: 2 }}>
              Sign In
            </Button>
            <Button variant="contained" fullWidth>
              Start Free Trial
            </Button>
          </Box>
        )}
      </Container>
    </AppBar>
  );

  // Enhanced Hero Section
  const HeroSection = () => (
    <Box
      id="home"
      className="hero-gradient"
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        pt: 10,
        position: 'relative',
        overflow: 'hidden',
      }}
    >
      {/* Background Elements */}
      {/* <Box
        sx={{
          position: 'absolute',
          top: '10%',
          right: '10%',
          width: 300,
          height: 300,
          borderRadius: '50%',
          background: 'rgba(255, 255, 255, 0.1)',
          backdropFilter: 'blur(40px)',
          animation: 'float 8s ease-in-out infinite',
        }}
      /> */}
      {/* <Box
        sx={{
          position: 'absolute',
          bottom: '20%',
          left: '5%',
          width: 200,
          height: 200,
          borderRadius: '50%',
          background: 'rgba(255, 255, 255, 0.05)',
          backdropFilter: 'blur(40px)',
          animation: 'float 6s ease-in-out infinite reverse',
        }}
      /> */}

      <Container maxWidth="xl">
        <Grid container spacing={6} alignItems="center">
          <Grid item xs={12} md={6}>
            <Fade in timeout={1000}>
              <Box>
                <Typography
                  variant="h1"
                  sx={{
                    color: 'white',
                    fontWeight: 800,
                    mb: 3,
                    fontSize: { xs: '2.5rem', md: '4rem', lg: '4.5rem' },
                    lineHeight: 1.1,
                  }}
                >
                  Build Your{' '}
                  <Box
                    component="span"
                    sx={{
                      background: 'linear-gradient(45deg, #ffd700, #ffed4e)',
                      backgroundClip: 'text',
                      WebkitBackgroundClip: 'text',
                      WebkitTextFillColor: 'transparent',
                    }}
                  >
                    Dream Store
                  </Box>{' '}
                  in Minutes
                </Typography>
                
                <Typography
                  variant="h5"
                  sx={{
                    color: 'rgba(255, 255, 255, 0.9)',
                    mb: 4,
                    fontWeight: 400,
                    lineHeight: 1.6,
                    maxWidth: '600px',
                  }}
                >
                  The most powerful e-commerce platform designed for ambitious entrepreneurs. 
                  Create stunning online stores with AI-powered tools, advanced analytics, 
                  and seamless integrations.
                </Typography>

                <Box sx={{ display: 'flex', gap: 3, mb: 6, flexWrap: 'wrap' }}>
                  {/* <Button
                    variant="contained"
                    size="large"
                    endIcon={<ArrowForward />}
                    sx={{
                      background: 'rgba(255, 255, 255, 0.2)',
                      backdropFilter: 'blur(20px)',
                      color: 'white',
                      border: '1px solid rgba(255, 255, 255, 0.3)',
                      fontSize: '1.1rem',
                      px: 4,
                      py: 2,
                      '&:hover': {
                        background: 'rgba(255, 255, 255, 0.3)',
                        transform: 'translateY(-2px)',
                      }
                    }}
                  >
                    Start Building Today
                  </Button> */}
                  
                  {/* <Button
  component="a"
  href="mailto:support@shopifyv.com"
                    variant="outlined"
                    size="large"
                    startIcon={<Mail />}
                    sx={{
                      color: 'white',
                      borderColor: 'rgba(255, 255, 255, 0.5)',
                      fontSize: '1.1rem',
                      px: 4,
                      py: 2,
                      '&:hover': {
                        borderColor: 'white',
                        background: 'rgba(255, 255, 255, 0.1)',
                      }
                    }}
                  >
                    Talk to Sales
                  </Button> */}
                </Box>

                {/* Trust Indicators */}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 4, flexWrap: 'wrap' }}>
                  {/* <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Rating value={5} readOnly size="small" />
                    <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.8)' }}>
                      4.9/5 from 10,000+ reviews
                    </Typography>
                  </Box> */}
                  {/* <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <CheckCircle sx={{ color: '#10b981', fontSize: 20 }} />
                    <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.8)' }}>
                      Free 14-day trial
                    </Typography>
                  </Box> */}
                </Box>
              </Box>
            </Fade>
          </Grid>

          <Grid item xs={12} md={6}>
            <Slide direction="left" in timeout={1200}>
              <Box
                sx={{
                  position: 'relative',
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                }}
              >
                {/* Mock Dashboard/Store Preview */}
                <Box
                  className="floating-animation glass-effect"
                  sx={{
                    width: '100%',
                    maxWidth: 500,
                    borderRadius: 4,
                    p: 3,
                    background: 'rgba(255, 255, 255, 0.1)',
                    backdropFilter: 'blur(20px)',
                    border: '1px solid rgba(255, 255, 255, 0.2)',
                  }}
                >
                  <Box sx={{ display: 'flex', gap: 1, mb: 3 }}>
                    <Box sx={{ width: 12, height: 12, borderRadius: '50%', bgcolor: '#ff5f57' }} />
                    <Box sx={{ width: 12, height: 12, borderRadius: '50%', bgcolor: '#ffbd2e' }} />
                    <Box sx={{ width: 12, height: 12, borderRadius: '50%', bgcolor: '#28ca42' }} />
                  </Box>
                  
                  <Box sx={{ mb: 3 }}>
                    <Typography variant="h6" sx={{ color: 'white', mb: 2 }}>
                      Your Store Dashboard
                    </Typography>
                    <LinearProgress 
                      variant="determinate" 
                      value={85} 
                      sx={{ 
                        height: 8, 
                        borderRadius: 4,
                        background: 'rgba(255, 255, 255, 0.2)',
                        '& .MuiLinearProgress-bar': {
                          background: 'linear-gradient(90deg, #10b981, #34d399)'
                        }
                      }} 
                    />
                    <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.7)', mt: 1 }}>
                      Setup Progress: 85%
                    </Typography>
                  </Box>

                  <Grid container spacing={2}>
                    <Grid item xs={6}>
                      <Box 
                        sx={{ 
                          p: 2, 
                          borderRadius: 2, 
                          background: 'rgba(255, 255, 255, 0.1)',
                          textAlign: 'center'
                        }}
                      >
                        <TrendingUp sx={{ color: '#10b981', fontSize: 32, mb: 1 }} />
                        <Typography variant="h6" sx={{ color: 'white' }}>$12.5K</Typography>
                        <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
                          Revenue
                        </Typography>
                      </Box>
                    </Grid>
                    <Grid item xs={6}>
                      <Box 
                        sx={{ 
                          p: 2, 
                          borderRadius: 2, 
                          background: 'rgba(255, 255, 255, 0.1)',
                          textAlign: 'center'
                        }}
                      >
                        <Groups sx={{ color: '#6366f1', fontSize: 32, mb: 1 }} />
                        <Typography variant="h6" sx={{ color: 'white' }}>1K+</Typography>
                        <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
                          Customers
                        </Typography>
                      </Box>
                    </Grid>
                  </Grid>
                </Box>
              </Box>
            </Slide>
          </Grid>
        </Grid>
      </Container>
    </Box>
  );

  // Enhanced Stats Section
  const StatsSection = () => {
    const stats = [
      { icon: Store, number: '500+', label: 'Active Stores', color: '#667eea' },
      { icon: TrendingUp, number: '$50K+', label: 'GMV Processed', color: '#10b981' },
      { icon: Public, number: '20+', label: 'Countries', color: '#f59e0b' },
      { icon: Support, number: '24/7', label: 'Expert Support', color: '#ef4444' }
    ];

    return (
      <Container maxWidth="xl" sx={{ py: 8, position: 'relative', zIndex: 2 }}>
        <Grid container spacing={4}>
          {stats.map((stat, index) => (
            <Grid item xs={6} md={3} key={index}>
              <Card
                sx={{
                  p: 4,
                  textAlign: 'center',
                  background: 'rgba(255, 255, 255, 0.9)',
                  backdropFilter: 'blur(20px)',
                  border: '1px solid rgba(255, 255, 255, 0.2)',
                }}
              >
                <Box
                  sx={{
                    width: 80,
                    height: 80,
                    borderRadius: '50%',
                    background: `linear-gradient(135deg, ${stat.color}, ${stat.color}99)`,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    mx: 'auto',
                    mb: 2,
                  }}
                >
                  <stat.icon sx={{ fontSize: 40, color: 'white' }} />
                </Box>
                <Typography variant="h3" sx={{ fontWeight: 800, mb: 1, color: stat.color }}>
                  {stat.number}
                </Typography>
                <Typography variant="body1" sx={{ color: 'text.secondary', fontWeight: 500 }}>
                  {stat.label}
                </Typography>
              </Card>
            </Grid>
          ))}
        </Grid>
      </Container>
    );
  };

  // Enhanced Features Section
  const FeaturesSection = () => {
    const features = [
      {
        icon: DesignServices,
        title: 'AI-Powered Design',
        description: 'Create stunning stores with our intelligent design assistant that adapts to your brand.',
        gradient: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
      },
      {
        icon: Analytics,
        title: 'Advanced Analytics',
        description: 'Get deep insights into customer behavior with real-time analytics and predictive forecasting.',
        gradient: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
      },
      {
        icon: Security,
        title: 'Enterprise Security',
        description: 'Bank-level security with SSL certificates, PCI compliance, and fraud protection.',
        gradient: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)',
      },
      {
        icon: Rocket,
        title: 'Lightning Fast',
        description: 'Optimized for speed with global CDN, image compression, and performance monitoring.',
        gradient: 'linear-gradient(135deg, #43e97b 0%, #38f9d7 100%)',
      },
      {
        icon: Language,
        title: 'Multi-Currency',
        description: 'Sell globally with support for 100+ currencies and automatic exchange rates.',
        gradient: 'linear-gradient(135deg, #fa709a 0%, #fee140 100%)',
      },
      {
        icon: ConnectWithoutContact,
        title: 'Seamless Integrations',
        description: 'Connect with 500+ apps including CRM, email marketing, and inventory management.',
        gradient: 'linear-gradient(135deg, #a8edea 0%, #fed6e3 100%)',
      }
    ];

    return (
      <Box id="features" sx={{ py: 12, background: 'linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)' }}>
        <Container maxWidth="xl">
          <Box sx={{ textAlign: 'center', mb: 8 }}>
            <Typography
              variant="h2"
              sx={{
                fontWeight: 800,
                mb: 3,
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                backgroundClip: 'text',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
              }}
            >
              Everything You Need to Succeed
            </Typography>
            <Typography
              variant="h6"
              sx={{
                color: 'text.secondary',
                maxWidth: 600,
                mx: 'auto',
                lineHeight: 1.6,
              }}
            >
              Powerful features designed to help you build, manage, and scale your online business effortlessly.
            </Typography>
          </Box>

          <Grid container spacing={4}>
            {features.map((feature, index) => (
              <Grid item xs={12} md={6} lg={4} key={index}>
                <Card
                  sx={{
                    height: '100%',
                    p: 4,
                    background: 'rgba(255, 255, 255, 0.8)',
                    backdropFilter: 'blur(20px)',
                    border: '1px solid rgba(255, 255, 255, 0.2)',
                    position: 'relative',
                    overflow: 'hidden',
                    '&:before': {
                      content: '""',
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      right: 0,
                      height: 4,
                      background: feature.gradient,
                    }
                  }}
                >
                  <Box
                    sx={{
                      width: 60,
                      height: 60,
                      borderRadius: 3,
                      background: feature.gradient,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      mb: 3,
                    }}
                  >
                    <feature.icon sx={{ fontSize: 30, color: 'white' }} />
                  </Box>
                  
                  <Typography variant="h5" sx={{ fontWeight: 600, mb: 2 }}>
                    {feature.title}
                  </Typography>
                  
                  <Typography variant="body1" sx={{ color: 'text.secondary', lineHeight: 1.6 }}>
                    {feature.description}
                  </Typography>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>
    );
  };

  // Enhanced Solutions Section
  const SolutionsSection = () => {
    const solutions = [
      {
        title: 'Fashion & Apparel',
        description: 'Showcase your collections with stunning galleries, size guides, and virtual try-on features.',
        icon: Palette,
        image: 'https://images.unsplash.com/photo-1441986300917-64674bd600d8?w=400&h=300&fit=crop',
        metrics: '+45% conversion rate',
        color: '#f093fb'
      },
      {
        title: 'Electronics & Tech',
        description: 'Advanced product configurators, warranty management, and technical specifications.',
        icon: Devices,
        image: 'https://images.unsplash.com/photo-1468495244123-6c6c332eeece?w=400&h=300&fit=crop',
        metrics: '+60% average order value',
        color: '#4facfe'
      },
      {
        title: 'Health & Beauty',
        description: 'Subscription boxes, ingredient transparency, and personalized product recommendations.',
        icon: Favorite,
        image: 'https://images.unsplash.com/photo-1556228453-efd6c1ff04f6?w=400&h=300&fit=crop',
        metrics: '+35% customer retention',
        color: '#43e97b'
      },
      {
        title: 'Food & Beverage',
        description: 'Local delivery integration, nutritional information, and inventory management.',
        icon: LocalShipping,
        image: 'https://images.unsplash.com/photo-1555939594-58d7cb561ad1?w=400&h=300&fit=crop',
        metrics: '+50% repeat orders',
        color: '#fee140'
      }
    ];

    return (
      <Box id="solutions" sx={{ py: 12 }}>
        <Container maxWidth="xl">
          <Box sx={{ textAlign: 'center', mb: 8 }}>
            <Typography
              variant="h2"
              sx={{
                fontWeight: 800,
                mb: 3,
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                backgroundClip: 'text',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
              }}
            >
              Industry-Specific Solutions
            </Typography>
            <Typography
              variant="h6"
              sx={{
                color: 'text.secondary',
                maxWidth: 600,
                mx: 'auto',
                lineHeight: 1.6,
              }}
            >
              Tailored features and tools designed for your specific industry needs and challenges.
            </Typography>
          </Box>

          <Grid container spacing={4}>
            {solutions.map((solution, index) => (
              <Grid item xs={12} md={6} key={index}>
                <Card
                  sx={{
                    height: '100%',
                    borderRadius: 4,
                    overflow: 'hidden',
                    position: 'relative',
                    background: 'rgba(255, 255, 255, 0.9)',
                    backdropFilter: 'blur(20px)',
                  }}
                >
                  <CardMedia
                    component="img"
                    height="200"
                    image={solution.image}
                    alt={solution.title}
                    sx={{
                      filter: 'brightness(0.8)',
                      transition: 'all 0.3s ease',
                      '&:hover': {
                        filter: 'brightness(1)',
                        transform: 'scale(1.05)',
                      }
                    }}
                  />
                  
                  <CardContent sx={{ p: 4 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                      <Box
                        sx={{
                          width: 50,
                          height: 50,
                          borderRadius: 2,
                          background: `linear-gradient(135deg, ${solution.color}, ${solution.color}99)`,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          mr: 2,
                        }}
                      >
                        <solution.icon sx={{ fontSize: 24, color: 'white' }} />
                      </Box>
                      <Typography variant="h5" sx={{ fontWeight: 600 }}>
                        {solution.title}
                      </Typography>
                    </Box>
                    
                    <Typography variant="body1" sx={{ color: 'text.secondary', mb: 3, lineHeight: 1.6 }}>
                      {solution.description}
                    </Typography>
                    
                    <Chip
                      label={solution.metrics}
                      sx={{
                        background: `linear-gradient(135deg, ${solution.color}, ${solution.color}99)`,
                        color: 'white',
                        fontWeight: 600,
                      }}
                    />
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>
    );
  };

  // Enhanced Testimonials Section
  const TestimonialsSection = () => {
    const testimonials = [
      {
        name: 'Sarah Johnson',
        role: 'Founder, EcoStyle',
        avatar: 'https://images.unsplash.com/photo-1494790108755-2616b612b1ca?w=100&h=100&fit=crop&crop=face',
        content: 'Shopifyv transformed our small fashion boutique into a thriving online business. The AI-powered design tools helped us create a professional store in just hours.',
        rating: 5,
        company: 'EcoStyle Fashion'
      },
      {
        name: 'Michael Chen',
        role: 'CEO, TechGear Pro',
        avatar: 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=100&h=100&fit=crop&crop=face',
        content: 'The analytics dashboard is incredible. We increased our conversion rate by 60% using the insights and A/B testing features. Best investment we ever made.',
        rating: 5,
        company: 'TechGear Pro'
      },
      {
        name: 'Emily Rodriguez',
        role: 'Marketing Director, BeautyBloom',
        avatar: 'https://images.unsplash.com/photo-1438761681033-6461ffad8d80?w=100&h=100&fit=crop&crop=face',
        content: 'Customer support is outstanding. The platform is intuitive, and the multi-currency support helped us expand to 15 countries within 6 months.',
        rating: 5,
        company: 'BeautyBloom'
      }
    ];

    return (
      <Box 
        id="testimonials" 
        sx={{ 
          py: 12, 
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          position: 'relative',
          overflow: 'hidden',
        }}
      >
        {/* Background Elements */}
        <Box
          sx={{
            position: 'absolute',
            top: '20%',
            right: '-5%',
            width: 400,
            height: 400,
            borderRadius: '50%',
            background: 'rgba(255, 255, 255, 0.05)',
            backdropFilter: 'blur(40px)',
          }}
        />

        <Container maxWidth="xl">
          <Box sx={{ textAlign: 'center', mb: 8 }}>
            <Typography
              variant="h2"
              sx={{
                fontWeight: 800,
                mb: 3,
                color: 'white',
              }}
            >
              Loved by Entrepreneurs Worldwide
            </Typography>
            <Typography
              variant="h6"
              sx={{
                color: 'rgba(255, 255, 255, 0.8)',
                maxWidth: 600,
                mx: 'auto',
                lineHeight: 1.6,
              }}
            >
              Join thousands of successful businesses that trust Shopifyv to power their growth.
            </Typography>
          </Box>

          <Grid container spacing={4}>
            {testimonials.map((testimonial, index) => (
              <Grid item xs={12} md={4} key={index}>
                <Card
                  sx={{
                    height: '100%',
                    p: 4,
                    background: 'rgba(255, 255, 255, 0.1)',
                    backdropFilter: 'blur(20px)',
                    border: '1px solid rgba(255, 255, 255, 0.2)',
                    color: 'white',
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                    <Avatar
                      src={testimonial.avatar}
                      sx={{ width: 60, height: 60, mr: 2 }}
                    />
                    <Box>
                      <Typography variant="h6" sx={{ fontWeight: 600 }}>
                        {testimonial.name}
                      </Typography>
                      <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
                        {testimonial.role}
                      </Typography>
                      <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.6)' }}>
                        {testimonial.company}
                      </Typography>
                    </Box>
                  </Box>
                  
                  <Rating value={testimonial.rating} readOnly sx={{ mb: 2 }} />
                  
                  <Typography variant="body1" sx={{ lineHeight: 1.6, fontStyle: 'italic' }}>
                    "{testimonial.content}"
                  </Typography>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>
    );
  };

  // Enhanced Resources Section
  const ResourcesSection = () => {
    const resources = [
      {
        category: 'Learning',
        icon: School,
        items: [
          { name: 'Getting Started Guide', description: 'Complete setup tutorial for beginners' },
          { name: 'Advanced Customization', description: 'Deep dive into themes and code' },
          { name: 'Marketing Masterclass', description: 'Proven strategies to grow your store' },
          { name: 'Analytics Workshop', description: 'Understanding your data and metrics' }
        ]
      },
      {
        category: 'Documentation',
        icon: MenuBook,
        items: [
          { name: 'API Reference', description: 'Complete developer documentation' },
          { name: 'Theme Development', description: 'Build custom themes from scratch' },
          { name: 'App Integration', description: 'Connect third-party applications' },
          { name: 'Best Practices', description: 'Optimization tips and tricks' }
        ]
      },
      {
        category: 'Community',
        icon: Groups,
        items: [
          { name: 'Developer Forum', description: 'Connect with other developers' },
          { name: 'Success Stories', description: 'Learn from successful merchants' },
          { name: 'Webinar Series', description: 'Live sessions with experts' },
          { name: 'Partner Network', description: 'Find certified developers and agencies' }
        ]
      }
    ];

    return (
      <Box id="resources" sx={{ py: 12, background: '#f8fafc' }}>
        <Container maxWidth="xl">
          <Box sx={{ textAlign: 'center', mb: 8 }}>
            <Typography
              variant="h2"
              sx={{
                fontWeight: 800,
                mb: 3,
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                backgroundClip: 'text',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
              }}
            >
              Resources & Support
            </Typography>
            <Typography
              variant="h6"
              sx={{
                color: 'text.secondary',
                maxWidth: 600,
                mx: 'auto',
                lineHeight: 1.6,
              }}
            >
              Everything you need to learn, build, and grow your business with comprehensive guides and community support.
            </Typography>
          </Box>

          <Grid container spacing={4}>
            {resources.map((resource, index) => (
              <Grid item xs={12} md={4} key={index}>
                <Card
                  sx={{
                    height: '100%',
                    p: 4,
                    background: 'rgba(255, 255, 255, 0.8)',
                    backdropFilter: 'blur(20px)',
                    border: '1px solid rgba(255, 255, 255, 0.2)',
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                    <Box
                      sx={{
                        width: 50,
                        height: 50,
                        borderRadius: 2,
                        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        mr: 2,
                      }}
                    >
                      <resource.icon sx={{ fontSize: 24, color: 'white' }} />
                    </Box>
                    <Typography variant="h5" sx={{ fontWeight: 600 }}>
                      {resource.category}
                    </Typography>
                  </Box>
                  
                  <List sx={{ p: 0 }}>
                    {resource.items.map((item, itemIndex) => (
                      <ListItem key={itemIndex} sx={{ px: 0, py: 1 }}>
                        <ListItemIcon sx={{ minWidth: 40 }}>
                          <CheckCircle sx={{ color: 'success.main', fontSize: 20 }} />
                        </ListItemIcon>
                        <ListItemText
                          primary={item.name}
                          secondary={item.description}
                          primaryTypographyProps={{ fontWeight: 600 }}
                        />
                      </ListItem>
                    ))}
                  </List>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>
    );
  };

  // FAQ Section
  const FAQSection = () => {
    const faqs = [
      {
        question: 'How quickly can I set up my store?',
        answer: 'With our AI-powered setup wizard, you can have a fully functional store running in under 30 minutes. Our templates and automated configurations handle the technical setup while you focus on adding your products and content.'
      },
      {
        question: 'What payment methods do you support?',
        answer: 'We support all major payment gateways including Stripe, PayPal, Apple Pay, Google Pay, and over 100 local payment methods across different countries. All transactions are secured with bank-level encryption.'
      },
      {
        question: 'Can I customize my store design?',
        answer: 'Absolutely! Choose from 200+ professional themes or create your own with our drag-and-drop builder. Advanced users can access full HTML/CSS customization and our developer APIs for unlimited possibilities.'
      },
      {
        question: 'Is there a transaction fee?',
        answer: 'We offer competitive rates starting from 2.4% + 30¢ per transaction. Higher tier plans have lower rates, and you can use your own payment processor to avoid our transaction fees entirely.'
      },
      {
        question: 'What kind of support do you provide?',
        answer: '24/7 support via chat, email, and phone. Plus access to our extensive knowledge base, video tutorials, community forums, and free expert consultations for business growth strategies.'
      }
    ];

    return (
      <Container maxWidth="lg" sx={{ py: 12 }}>
        <Box sx={{ textAlign: 'center', mb: 8 }}>
          <Typography
            variant="h2"
            sx={{
              fontWeight: 800,
              mb: 3,
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              backgroundClip: 'text',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
            }}
          >
            Frequently Asked Questions
          </Typography>
        </Box>

        {faqs.map((faq, index) => (
          <Accordion
            key={index}
            sx={{
              mb: 2,
              borderRadius: 2,
              '&:before': { display: 'none' },
              boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
            }}
          >
            <AccordionSummary expandIcon={<ExpandMore />}>
              <Typography variant="h6" sx={{ fontWeight: 600 }}>
                {faq.question}
              </Typography>
            </AccordionSummary>
            <AccordionDetails>
              <Typography variant="body1" sx={{ color: 'text.secondary', lineHeight: 1.6 }}>
                {faq.answer}
              </Typography>
            </AccordionDetails>
          </Accordion>
        ))}
      </Container>
    );
  };

  // CTA Section
  const CTASection = () => (
    <Box
      sx={{
        py: 12,
        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
        textAlign: 'center',
      }}
    >
      <Container maxWidth="md">
        <Typography
          variant="h2"
          sx={{
            fontWeight: 800,
            mb: 3,
            color: 'white',
          }}
        >
          Ready to Start Your Journey?
        </Typography>
        <Typography
          variant="h6"
          sx={{
            color: 'rgba(255, 255, 255, 0.8)',
            mb: 6,
            lineHeight: 1.6,
          }}
        >
          Join millions of entrepreneurs who have built successful businesses with Shopifyv. 
          Start your free trial today - no credit card required.
        </Typography>
        
        <Box sx={{ display: 'flex', gap: 3, justifyContent: 'center', flexWrap: 'wrap' }}>
          {/* <Button
            variant="contained"
            size="large"
            endIcon={<ArrowForward />}
            sx={{
              background: 'rgba(255, 255, 255, 0.2)',
              backdropFilter: 'blur(20px)',
              color: 'white',
              border: '1px solid rgba(255, 255, 255, 0.3)',
              fontSize: '1.1rem',
              px: 4,
              py: 2,
              '&:hover': {
                background: 'rgba(255, 255, 255, 0.3)',
              }
            }}
          >
            Start Free Trial
          </Button> */}
          
          <Button
  component="a"
  href="mailto:support@shopifyv.com"
  variant="outlined"
  size="large"
  startIcon={<Mail />}
  sx={{
    color: 'white',
    borderColor: 'rgba(255, 255, 255, 0.5)',
    fontSize: '1.1rem',
    px: 4,
    py: 2,
    '&:hover': {
      borderColor: 'white',
      background: 'rgba(255, 255, 255, 0.1)',
    }
  }}
>
  Talk to Sales
</Button>
        </Box>
      </Container>
    </Box>
  );

  // Enhanced Footer
  const Footer = () => (
    <Box id="contact" sx={{ bgcolor: '#1f2937', color: 'white', py: 8 }}>
      <Container maxWidth="xl">
        <Grid container spacing={6}>
          <Grid item xs={12} lg={4}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
              <Box
                sx={{
                  width: 50,
                  height: 50,
                  borderRadius: '12px',
                  background: 'white',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: 'white',
                  fontSize: '1.5rem',
                  fontWeight: 'bold',
                }}
              >
                <img src={require('./logo.png')} style={{
                  width: 40,
                  height: 40,
                  borderRadius: '12px',
                }}/>
              </Box>
              <Typography variant="h4" sx={{ fontWeight: 800 }}>
                Shopifyv
              </Typography>
            </Box>
            {/* <Typography variant="body1" sx={{ color: '#9ca3af', mb: 4, lineHeight: 1.6 }}>
              Empowering entrepreneurs worldwide with the most advanced e-commerce platform. 
              Build, manage, and scale your online business with confidence.
            </Typography> */}
            
            {/* <Box sx={{ display: 'flex', gap: 2 }}>
              {[Facebook, Twitter, LinkedIn, Instagram, YouTube, GitHub].map((Icon, index) => (
                <IconButton
                  key={index}
                  sx={{
                    bgcolor: 'rgba(255, 255, 255, 0.1)',
                    color: 'white',
                    '&:hover': {
                      bgcolor: 'rgba(255, 255, 255, 0.2)',
                      transform: 'translateY(-2px)',
                    }
                  }}
                >
                  <Icon />
                </IconButton>
              ))}
            </Box> */}
          </Grid>
{/* 
          <Grid item xs={12} sm={6} lg={2}>
            <Typography variant="h6" sx={{ fontWeight: 600, mb: 3 }}>
              Product
            </Typography>
            <List sx={{ p: 0 }}>
              {['Features', 'Templates', 'Integrations', 'Analytics', 'Mobile App'].map((item) => (
                <ListItem key={item} sx={{ px: 0, py: 0.5 }}>
                  <ListItemText
                    primary={item}
                    primaryTypographyProps={{
                      color: '#9ca3af',
                      '&:hover': { color: 'white', cursor: 'pointer' }
                    }}
                  />
                </ListItem>
              ))}
            </List>
          </Grid>

          <Grid item xs={12} sm={6} lg={2}>
            <Typography variant="h6" sx={{ fontWeight: 600, mb: 3 }}>
              Solutions
            </Typography>
            <List sx={{ p: 0 }}>
              {['Fashion', 'Electronics', 'Beauty', 'Food & Beverage', 'B2B'].map((item) => (
                <ListItem key={item} sx={{ px: 0, py: 0.5 }}>
                  <ListItemText
                    primary={item}
                    primaryTypographyProps={{
                      color: '#9ca3af',
                      '&:hover': { color: 'white', cursor: 'pointer' }
                    }}
                  />
                </ListItem>
              ))}
            </List>
          </Grid>

          <Grid item xs={12} sm={6} lg={2}>
            <Typography variant="h6" sx={{ fontWeight: 600, mb: 3 }}>
              Resources
            </Typography>
            <List sx={{ p: 0 }}>
              {['Documentation', 'API Reference', 'Community', 'Blog', 'Help Center'].map((item) => (
                <ListItem key={item} sx={{ px: 0, py: 0.5 }}>
                  <ListItemText
                    primary={item}
                    primaryTypographyProps={{
                      color: '#9ca3af',
                      '&:hover': { color: 'white', cursor: 'pointer' }
                    }}
                  />
                </ListItem>
              ))}
            </List>
          </Grid>

          <Grid item xs={12} sm={6} lg={2}>
            <Typography variant="h6" sx={{ fontWeight: 600, mb: 3 }}>
              Company
            </Typography>
            <List sx={{ p: 0 }}>
              {['About Us', 'Careers', 'Partners', 'Press', 'Contact'].map((item) => (
                <ListItem key={item} sx={{ px: 0, py: 0.5 }}>
                  <ListItemText
                    primary={item}
                    primaryTypographyProps={{
                      color: '#9ca3af',
                      '&:hover': { color: 'white', cursor: 'pointer' }
                    }}
                  />
                </ListItem>
              ))}
            </List>
          </Grid> */}
        </Grid>

        <Divider sx={{ my: 6, borderColor: '#374151' }} />

        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
          <Typography variant="body2" sx={{ color: '#9ca3af' }}>
            © 2022 Shopifyv. All rights reserved.
          </Typography>
          <Box sx={{ display: 'flex', gap: 3 }}>
            {/* <Typography variant="body2" sx={{ color: '#9ca3af' }}>
              Privacy Policy
            </Typography>
            <Typography variant="body2" sx={{ color: '#9ca3af' }}>
              Terms of Service
            </Typography>
            <Typography variant="body2" sx={{ color: '#9ca3af' }}>
              Cookie Policy
            </Typography> */}
          </Box>
        </Box>
      </Container>
    </Box>
  );

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      {globalStyles}
      <Header />
      <HeroSection />
      {/* <StatsSection /> */}
      <FeaturesSection />
      <SolutionsSection />
      {/* <TestimonialsSection /> */}
      <ResourcesSection />
      <FAQSection />
      <CTASection />
      <Footer />
    </ThemeProvider>
  );
};

export default ShopifyvWebsite;
